/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/label-has-associated-control, react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBlogs, removeBlog, updateBlog } from '../slice/blogSlice';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaFileAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmModal from '../common/ConfirmModal';
import './BlogList.css';

const BlogList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { data: blogs = [], loading } = useSelector((state) => state.blogs);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);
  const [selectedBlogs, setSelectedBlogs] = useState([]);
  
  // Custom Confirmation Modal State
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    dispatch(getBlogs());
  }, [dispatch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedBlogs(blogs.map(b => b.id));
    } else {
      setSelectedBlogs([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedBlogs.includes(id)) {
      setSelectedBlogs(selectedBlogs.filter(item => item !== id));
    } else {
      setSelectedBlogs([...selectedBlogs, id]);
    }
  };

  const handleToggleStatus = (blog) => {
    const updatedStatus = blog.status === 1 ? 0 : 1;
    dispatch(updateBlog({
      id: blog.id,
      data: {
        ...blog,
        status: updatedStatus
      }
    }));
  };

  const triggerDeleteConfirm = (e, blog) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteTarget(blog);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(removeBlog(deleteTarget.id));
      toast.success('Blog deleted successfully');
      setDeleteTarget(null);
    }
  };

  const handleConfirmEdit = () => {
    if (editTarget) {
      const id = editTarget.id;
      setEditTarget(null);
      navigate(`/edit-blog/${id}`);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return 'https://via.placeholder.com/50x50?text=No+Img';
    const backendHost = process.env.REACT_APP_BACKEND 
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '') 
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, '0');
    return `${day}/${month}/${year} ${formattedHours}:${minutes} ${ampm}`;
  };

  const filteredBlogs = blogs.filter(blog => 
    blog.title && blog.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
      <div className="premium-page-container animate-fade-up">
      {/* Header Section */}
      <div className="premium-page-header">
        <h1 className="page-title">Blog Manager</h1>
        <button className="btn-create" onClick={() => navigate('/add-blog')}>
          <FaPlus style={{ marginRight: '8px' }} /> Create
        </button>
      </div>

      {/* Search & Actions Panel */}
      <div className="premium-controls-bar">
        <div className="search-bar-wrapper search-box" style={{ margin: 0 }}>
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder="Search here" 
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>

      {/* Blog Table Area */}
      <div className="premium-table-wrapper">
        {loading ? (
          <div className="loading-spinner" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>Loading blogs...</div>
        ) : filteredBlogs.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem', textAlign: 'center', color: '#c4a882' }}>No blogs found. Click "+ Create" to add one.</div>
        ) : (
            <table className="premium-table">
              <thead>
                <tr>
                  <th style={{ width: '4%' }}>
                    <input 
                      type="checkbox" 
                      className="premium-checkbox"
                      onChange={handleSelectAll} 
                      checked={selectedBlogs.length === blogs.length && blogs.length > 0}
                    />
                  </th>
                  <th style={{ width: '12%' }}>Status</th>
                  <th style={{ width: '32%' }}>Blog Title</th>
                  <th style={{ width: '21%' }}>Created on</th>
                  <th style={{ width: '21%' }}>Last Updated on</th>
                  <th style={{ width: '10%', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentBlogs.map((blog) => (
                  <tr key={blog.id} className={selectedBlogs.includes(blog.id) ? 'selected-row' : ''}>
                    <td>
                      <input 
                        type="checkbox" 
                        className="premium-checkbox"
                        checked={selectedBlogs.includes(blog.id)}
                        onChange={() => handleSelectOne(blog.id)}
                      />
                    </td>
                    <td>
                      <div className="status-toggle-wrapper">
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="status-toggle" htmlFor={`status-toggle-${blog.id}`}>
                          <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', border: 0 }}>Toggle Status</span>
                          <input 
                            id={`status-toggle-${blog.id}`}
                            type="checkbox" 
                            checked={blog.status === 1} 
                            onChange={() => handleToggleStatus(blog)}
                          />
                          <span className="status-slider"></span>
                        </label>
                        <span className={`status-label ${blog.status === 1 ? 'active' : 'inactive'}`}>
                          {blog.status === 1 ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="blog-title-cell">
                        <img 
                          src={getImageUrl(blog.thumb)} 
                          alt={blog.title} 
                          className="blog-thumb-img" 
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50x50?text=No+Img';
                          }}
                        />
                        <span className="blog-title-text" title={blog.title}>{blog.title}</span>
                      </div>
                    </td>
                    <td className="nowrap-cell">{formatDate(blog.created_on)}</td>
                    <td className="nowrap-cell">{formatDate(blog.updated_on)}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="inline-actions-container" style={{ justifyContent: 'center' }}>
                        <button 
                          className="btn-inline-action edit-action" 
                          onClick={() => setEditTarget(blog)}
                          title="Modify Basic Info"
                        >
                          <FaEdit size={13} />
                        </button>
                        <button 
                          className="btn-inline-action" 
                          onClick={() => navigate(`/modify-blog-content/${blog.id}`)}
                          title="Modify Content"
                          style={{ color: '#60a5fa' }}
                        >
                          <FaFileAlt size={13} />
                        </button>
                        <button 
                          className="btn-inline-action delete-action" 
                          onClick={(e) => triggerDeleteConfirm(e, blog)}
                          title="Delete Blog"
                        >
                          <FaTrash size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination Controls */}
          {filteredBlogs.length > 0 && (
            <div className="premium-pagination-container" style={{ margin: '1.5rem 0 0 0' }}>
              <div className="premium-pagination-info">
                Showing <span>{indexOfFirstItem + 1}</span> to <span>{Math.min(indexOfLastItem, filteredBlogs.length)}</span> of <span>{filteredBlogs.length}</span> entries
              </div>
              <div className="premium-pagination-list">
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(1)} 
                  disabled={currentPage === 1}
                >
                  &laquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                  disabled={currentPage === 1}
                >
                  &lsaquo;
                </button>
                
                {Array.from({ length: Math.ceil(filteredBlogs.length / itemsPerPage) }, (_, idx) => idx + 1)
                  .filter(page => {
                    const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
                    return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                  })
                  .map((page, index, array) => {
                    return (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span style={{ color: 'rgba(196, 168, 130, 0.4)', padding: '0 0.25rem' }}>...</span>
                        )}
                        <button 
                          className={`premium-pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredBlogs.length / itemsPerPage)))} 
                  disabled={currentPage === Math.ceil(filteredBlogs.length / itemsPerPage)}
                >
                  &rsaquo;
                </button>
                <button 
                  className="premium-pagination-btn arrow-btn" 
                  onClick={() => setCurrentPage(Math.ceil(filteredBlogs.length / itemsPerPage))} 
                  disabled={currentPage === Math.ceil(filteredBlogs.length / itemsPerPage)}
                >
                  &raquo;
                </button>
              </div>
            </div>
          )}
        </div>
    </div>

      {/* Confirmation Modals */}
      <ConfirmModal
        isOpen={!!editTarget}
        type="edit"
        title="Modify Blog"
        itemName={editTarget?.title}
        itemType="blog"
        onConfirm={handleConfirmEdit}
        onCancel={() => setEditTarget(null)}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        type="delete"
        title="Delete Blog"
        itemName={deleteTarget?.title}
        itemType="blog"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default BlogList;
